import { getWithdrawalSyncStatus, listWithdrawalServerFiles } from "../api/sync";
import { getAllLocalWithdrawals, getLocalWithdrawal, updateLocalWithdrawalAtomically } from "../storage/offlineDatabase";
import type { LocalWithdrawalRecord } from "../types/offlineWithdrawal";

const MB = 1024 * 1024;
const DAY_MS = 24 * 60 * 60 * 1000;

export const LOCAL_STORAGE_TARGET_BYTES = 750 * MB;
export const LOCAL_STORAGE_WARNING_BYTES = 1024 * MB;
export const LOCAL_STORAGE_CLEANUP_BYTES = 1536 * MB;
export const LOCAL_STORAGE_HARD_GUARD_BYTES = 2048 * MB;
export const LOCAL_STORAGE_CAPTURE_RESERVE_BYTES = 128 * MB;

const COMPLETED_DOCUMENT_KEEP_COUNT = 50;
const COMPLETED_DOCUMENT_KEEP_DAYS = 30;
const SYNCED_DRAFT_TECHNICAL_KEEP_DAYS = 14;
const MAX_ARCHIVE_VERIFICATIONS_PER_RUN = 4;
const STATUS_MAINTENANCE_THROTTLE_MS = 5 * 60_000;
let lastMaintenanceAt = 0;

const TECHNICAL_FIELDS = [
  ["source", "source_blob"],
  ["detection_source", "detection_source_blob"],
  ["analysis_input", "analysis_input_blob"],
  ["rectification_input", "rectification_input_blob"],
  ["ocr_warp", "ocr_warp_blob"],
  ["vision_feedback", "vision_feedback_blob"],
  ["live_training", "live_training_blob"],
  ["debug", "debug_image_blob"],
  ["debug_data", "debug_data_blob"],
] as const;

const DRAFT_SAFE_TECHNICAL_FIELDS = [
  ["source", "source_blob"],
  ["detection_source", "detection_source_blob"],
  ["analysis_input", "analysis_input_blob"],
  ["rectification_input", "rectification_input_blob"],
  ["live_training", "live_training_blob"],
  ["debug", "debug_image_blob"],
  ["debug_data", "debug_data_blob"],
] as const;

const DOCUMENT_FIELDS = [
  ["pdf", "pdf_blob"],
  ["pdf_preview", "pdf_preview_blob"],
  ["label", "label_blob"],
] as const;

type BlobField =
  | "pdf_blob"
  | "pdf_preview_blob"
  | "label_blob"
  | "source_blob"
  | "detection_source_blob"
  | "analysis_input_blob"
  | "rectification_input_blob"
  | "ocr_warp_blob"
  | "vision_feedback_blob"
  | "live_training_blob"
  | "debug_image_blob"
  | "debug_data_blob";

export interface LocalStorageRecordBreakdown {
  clientUuid: string;
  recordType: LocalWithdrawalRecord["record_type"];
  syncStatus: LocalWithdrawalRecord["sync_status"];
  updatedAt: string;
  totalBytes: number;
  protectedBytes: number;
  reclaimableBytes: number;
  files: Record<string, number>;
}

export interface LocalStorageLifecycleReport {
  usageBytes: number;
  quotaBytes: number;
  quotaHeadroomBytes: number;
  targetBytes: number;
  warningBytes: number;
  cleanupBytes: number;
  hardGuardBytes: number;
  protectedBytes: number;
  reclaimableBytes: number;
  withdrawalBytes: number;
  recordCount: number;
  draftCount: number;
  unsyncedCount: number;
  level: "ok" | "warning" | "critical";
  cleanupRecommended: boolean;
  hardGuardReached: boolean;
  records: LocalStorageRecordBreakdown[];
}

function blobSize(record: LocalWithdrawalRecord, field: BlobField): number {
  const value = record[field];
  return value instanceof Blob ? value.size : 0;
}

function recordAgeMs(record: LocalWithdrawalRecord): number {
  const stamp = Date.parse(record.updated_at || record.created_at);
  return Number.isFinite(stamp) ? Math.max(0, Date.now() - stamp) : 0;
}

function verifiedTypes(record: LocalWithdrawalRecord): Set<string> {
  return new Set(Array.isArray(record.server_archived_file_types) ? record.server_archived_file_types : []);
}

function canCompact(record: LocalWithdrawalRecord): boolean {
  return record.sync_status === "synced"
    && Boolean(record.server_withdrawal_id)
    && Boolean(record.server_archive_verified_at)
    && Array.isArray(record.server_archived_file_types);
}

function reclaimableTechnicalBytes(record: LocalWithdrawalRecord): number {
  if (!canCompact(record)) return 0;
  const serverTypes = verifiedTypes(record);
  const fields = record.record_type === "draft" ? DRAFT_SAFE_TECHNICAL_FIELDS : TECHNICAL_FIELDS;
  if (record.record_type === "draft" && recordAgeMs(record) < SYNCED_DRAFT_TECHNICAL_KEEP_DAYS * DAY_MS) return 0;
  let total = 0;
  for (const [fileType, field] of fields as ReadonlyArray<readonly [string, BlobField]>) {
    if (serverTypes.has(fileType)) total += blobSize(record, field);
  }
  return total;
}

function reclaimableDocumentBytes(record: LocalWithdrawalRecord, allowDocuments: boolean): number {
  if (!allowDocuments || record.record_type === "draft" || !canCompact(record)) return 0;
  const serverTypes = verifiedTypes(record);
  let total = 0;
  for (const [fileType, field] of DOCUMENT_FIELDS) {
    if (serverTypes.has(fileType)) total += blobSize(record, field);
  }
  return total;
}

function recordBlobBreakdown(record: LocalWithdrawalRecord, allowDocuments: boolean): LocalStorageRecordBreakdown {
  const files: Record<string, number> = {
    pdf: blobSize(record, "pdf_blob"),
    pdfPreview: blobSize(record, "pdf_preview_blob"),
    label: blobSize(record, "label_blob"),
    source: blobSize(record, "source_blob"),
    detectionSource: blobSize(record, "detection_source_blob"),
    analysisInput: blobSize(record, "analysis_input_blob"),
    rectificationInput: blobSize(record, "rectification_input_blob"),
    ocrWarp: blobSize(record, "ocr_warp_blob"),
    visionFeedback: blobSize(record, "vision_feedback_blob"),
    liveTraining: blobSize(record, "live_training_blob"),
    debugImage: blobSize(record, "debug_image_blob"),
    debugData: blobSize(record, "debug_data_blob"),
  };
  const totalBytes = Object.values(files).reduce((sum, value) => sum + value, 0);
  const reclaimableBytes = reclaimableTechnicalBytes(record) + reclaimableDocumentBytes(record, allowDocuments);
  return {
    clientUuid: record.client_uuid,
    recordType: record.record_type,
    syncStatus: record.sync_status,
    updatedAt: record.updated_at,
    totalBytes,
    protectedBytes: Math.max(0, totalBytes - reclaimableBytes),
    reclaimableBytes,
    files,
  };
}

function effectiveBudget(quotaBytes: number): { warning: number; cleanup: number; hard: number; target: number } {
  if (!quotaBytes) return {
    target: LOCAL_STORAGE_TARGET_BYTES,
    warning: LOCAL_STORAGE_WARNING_BYTES,
    cleanup: LOCAL_STORAGE_CLEANUP_BYTES,
    hard: LOCAL_STORAGE_HARD_GUARD_BYTES,
  };
  // Kleine Browser-Quotas werden relativ früher geschützt; auf großen Quotas
  // bleibt das absolute PWA-Budget deutlich unter den vom Nutzer unerwünschten GB-Größen.
  const hard = Math.min(LOCAL_STORAGE_HARD_GUARD_BYTES, Math.max(256 * MB, quotaBytes * 0.82));
  const cleanup = Math.min(LOCAL_STORAGE_CLEANUP_BYTES, hard * 0.78);
  const warning = Math.min(LOCAL_STORAGE_WARNING_BYTES, cleanup * 0.78);
  const target = Math.min(LOCAL_STORAGE_TARGET_BYTES, warning * 0.75);
  return { target, warning, cleanup, hard };
}

async function storageEstimate(): Promise<{ usage: number; quota: number }> {
  if (typeof navigator === "undefined" || !navigator.storage?.estimate) return { usage: 0, quota: 0 };
  const estimate = await navigator.storage.estimate().catch(() => undefined);
  return { usage: estimate?.usage ?? 0, quota: estimate?.quota ?? 0 };
}

export async function getLocalStorageLifecycleReport(): Promise<LocalStorageLifecycleReport> {
  const [records, estimate] = await Promise.all([
    getAllLocalWithdrawals().catch(() => [] as LocalWithdrawalRecord[]),
    storageEstimate(),
  ]);
  const budget = effectiveBudget(estimate.quota);
  const documentEligible = new Set(
    records
      .filter((record) => record.record_type !== "draft" && record.sync_status === "synced")
      .sort((a, b) => Date.parse(b.updated_at) - Date.parse(a.updated_at))
      .filter((record, index) => index >= COMPLETED_DOCUMENT_KEEP_COUNT && recordAgeMs(record) >= COMPLETED_DOCUMENT_KEEP_DAYS * DAY_MS)
      .map((record) => record.client_uuid),
  );
  const breakdown = records.map((record) => recordBlobBreakdown(record, documentEligible.has(record.client_uuid)));
  const withdrawalBytes = breakdown.reduce((sum, record) => sum + record.totalBytes, 0);
  const protectedBytes = breakdown.reduce((sum, record) => sum + record.protectedBytes, 0);
  const reclaimableBytes = breakdown.reduce((sum, record) => sum + record.reclaimableBytes, 0);
  const quotaHeadroomBytes = estimate.quota ? Math.max(0, estimate.quota - estimate.usage) : Number.POSITIVE_INFINITY;
  const hardGuardReached = estimate.usage >= budget.hard || quotaHeadroomBytes < LOCAL_STORAGE_CAPTURE_RESERVE_BYTES;
  const level = hardGuardReached ? "critical" : estimate.usage >= budget.warning ? "warning" : "ok";
  return {
    usageBytes: estimate.usage,
    quotaBytes: estimate.quota,
    quotaHeadroomBytes,
    targetBytes: budget.target,
    warningBytes: budget.warning,
    cleanupBytes: budget.cleanup,
    hardGuardBytes: budget.hard,
    protectedBytes,
    reclaimableBytes,
    withdrawalBytes,
    recordCount: records.length,
    draftCount: records.filter((record) => record.record_type === "draft").length,
    unsyncedCount: records.filter((record) => record.sync_status !== "synced").length,
    level,
    cleanupRecommended: estimate.usage >= budget.warning,
    hardGuardReached,
    records: breakdown.sort((a, b) => b.totalBytes - a.totalBytes),
  };
}

function clearBlobFields(
  record: LocalWithdrawalRecord,
  pairs: ReadonlyArray<readonly [string, BlobField]>,
  serverTypes: Set<string>,
): { next: LocalWithdrawalRecord; removedTypes: string[]; removedBytes: number } {
  const next = { ...record };
  const removedTypes: string[] = [];
  let removedBytes = 0;
  for (const [fileType, field] of pairs) {
    if (!serverTypes.has(fileType)) continue;
    const bytes = blobSize(record, field);
    if (bytes <= 0) continue;
    removedBytes += bytes;
    removedTypes.push(fileType);
    (next as unknown as Record<string, unknown>)[field] = null;
  }
  return { next, removedTypes, removedBytes };
}

async function compactRecord(record: LocalWithdrawalRecord, allowDocuments: boolean): Promise<number> {
  if (!canCompact(record)) return 0;
  const serverTypes = verifiedTypes(record);
  const technicalFields = record.record_type === "draft" ? DRAFT_SAFE_TECHNICAL_FIELDS : TECHNICAL_FIELDS;
  const draftTooRecent = record.record_type === "draft" && recordAgeMs(record) < SYNCED_DRAFT_TECHNICAL_KEEP_DAYS * DAY_MS;
  let next = record;
  let removedBytes = 0;
  const removedTypes: string[] = [];

  if (!draftTooRecent) {
    const technical = clearBlobFields(next, technicalFields, serverTypes);
    next = technical.next;
    removedBytes += technical.removedBytes;
    removedTypes.push(...technical.removedTypes);
  }
  if (allowDocuments && record.record_type !== "draft") {
    const documents = clearBlobFields(next, DOCUMENT_FIELDS, serverTypes);
    removedBytes += documents.removedBytes;
    removedTypes.push(...documents.removedTypes);
  }
  if (removedBytes <= 0) return 0;

  const expectedRevision = Math.max(0, record.content_revision ?? 0);
  let committedBytes = 0;
  await updateLocalWithdrawalAtomically(record.client_uuid, (current) => {
    if (current.sync_status !== "synced" || Math.max(0, current.content_revision ?? 0) !== expectedRevision) return null;
    const currentServerTypes = verifiedTypes(current);
    const currentTechnicalFields = current.record_type === "draft" ? DRAFT_SAFE_TECHNICAL_FIELDS : TECHNICAL_FIELDS;
    const currentDraftTooRecent = current.record_type === "draft" && recordAgeMs(current) < SYNCED_DRAFT_TECHNICAL_KEEP_DAYS * DAY_MS;
    let atomicNext = current;
    let atomicRemoved = 0;
    const atomicTypes: string[] = [];
    if (!currentDraftTooRecent) {
      const technical = clearBlobFields(atomicNext, currentTechnicalFields, currentServerTypes);
      atomicNext = technical.next;
      atomicRemoved += technical.removedBytes;
      atomicTypes.push(...technical.removedTypes);
    }
    if (allowDocuments && current.record_type !== "draft") {
      const documents = clearBlobFields(atomicNext, DOCUMENT_FIELDS, currentServerTypes);
      atomicNext = documents.next;
      atomicRemoved += documents.removedBytes;
      atomicTypes.push(...documents.removedTypes);
    }
    if (atomicRemoved <= 0) return null;
    const previous = new Set(Array.isArray(current.local_compacted_file_types) ? current.local_compacted_file_types : []);
    for (const fileType of atomicTypes) previous.add(fileType);
    committedBytes = atomicRemoved;
    return {
      ...atomicNext,
      local_compacted_at: new Date().toISOString(),
      local_compacted_file_types: [...previous].sort(),
      local_compacted_bytes_total: Math.max(0, current.local_compacted_bytes_total ?? 0) + atomicRemoved,
    };
  });
  return committedBytes;
}

export async function verifyServerArchiveAndCompact(clientUuid: string): Promise<number> {
  const record = await getLocalWithdrawal(clientUuid);
  if (!record || record.sync_status !== "synced" || !record.server_withdrawal_id) return 0;
  // Für Legacy-/späte Verifikation reicht "der Server hat inzwischen irgendeine
  // neuere Datei dieses Typs" ausdrücklich NICHT. Nur wenn der aktuelle
  // Server-Synczeitpunkt noch exakt zu diesem lokalen synchronisierten Snapshot
  // gehört, dürfen dessen Dateien die lokale Kopie freigeben. Ein später auf
  // einem anderen Gerät geänderter Vorgang bleibt damit lokal unangetastet.
  const serverState = await getWithdrawalSyncStatus(record.client_uuid);
  if (!serverState
    || serverState.withdrawal_id !== record.server_withdrawal_id
    || serverState.sync_status !== "synced"
    || !record.synced_at
    || !serverState.synced_at
    || Date.parse(serverState.synced_at) !== Date.parse(record.synced_at)) return 0;

  const files = await listWithdrawalServerFiles(record.server_withdrawal_id);
  const verifiedVersion = serverState.current_version;
  const currentFiles = files.filter((file) => file.version === verifiedVersion);
  if (!currentFiles.length) return 0;
  const fileTypes = [...new Set(currentFiles.map((file) => file.file_type))].sort();
  const expectedRevision = Math.max(0, record.content_revision ?? 0);
  const verified = await updateLocalWithdrawalAtomically(record.client_uuid, (current) => {
    if (current.sync_status !== "synced" || !current.server_withdrawal_id || Math.max(0, current.content_revision ?? 0) !== expectedRevision) return null;
    return {
      ...current,
      server_archive_verified_at: new Date().toISOString(),
      server_archive_version: verifiedVersion,
      server_archived_file_types: fileTypes,
    };
  });
  if (!verified || verified.sync_status !== "synced") return 0;
  return compactRecord(verified, false);
}

async function compactVerifiedRecords(records: LocalWithdrawalRecord[], allowDocuments: boolean): Promise<number> {
  let removed = 0;
  const synced = records
    .filter((record) => record.sync_status === "synced")
    .sort((a, b) => Date.parse(b.updated_at) - Date.parse(a.updated_at));
  const documentEligibleIds = new Set(
    synced
      .filter((record) => record.record_type !== "draft")
      .filter((record, index) => index >= COMPLETED_DOCUMENT_KEEP_COUNT && recordAgeMs(record) >= COMPLETED_DOCUMENT_KEEP_DAYS * DAY_MS)
      .map((record) => record.client_uuid),
  );
  for (const record of synced) {
    removed += await compactRecord(record, allowDocuments && documentEligibleIds.has(record.client_uuid));
  }
  return removed;
}

export async function runLocalStorageLifecycleMaintenance(options: {
  allowNetworkVerification?: boolean;
  reason?: "startup" | "status" | "capture" | "sync" | "manual";
} = {}): Promise<LocalStorageLifecycleReport> {
  if (options.reason === "status" && Date.now() - lastMaintenanceAt < STATUS_MAINTENANCE_THROTTLE_MS) {
    return getLocalStorageLifecycleReport();
  }
  lastMaintenanceAt = Date.now();

  // Server-derived Caches sind immer sicher begrenzbar. Visual-Lab-Testbilder
  // selbst bleiben Nutzer-/Admin-Daten und werden niemals automatisch gelöscht.
  await Promise.allSettled([
    import("./withdrawalHistoryService").then(({ pruneWithdrawalHistoryCache }) => pruneWithdrawalHistoryCache()),
    import("./visualLabTestSetService").then(({ pruneVisualLabBenchmarkHistory }) => pruneVisualLabBenchmarkHistory()),
    import("./cnnWebInferenceService").then(({ pruneCnnWebModelCache }) => pruneCnnWebModelCache()),
  ]);

  let records = await getAllLocalWithdrawals().catch(() => [] as LocalWithdrawalRecord[]);
  await compactVerifiedRecords(records, false);
  let report = await getLocalStorageLifecycleReport();

  // Erst wenn das Zielbudget überschritten ist, werden ältere bereits
  // synchronisierte Legacy-Records schrittweise serverseitig verifiziert.
  if (options.allowNetworkVerification && typeof navigator !== "undefined" && navigator.onLine && report.usageBytes >= report.warningBytes) {
    const unverified = records
      .filter((record) => record.sync_status === "synced" && record.server_withdrawal_id && !record.server_archive_verified_at)
      .sort((a, b) => Date.parse(a.updated_at) - Date.parse(b.updated_at))
      .slice(0, MAX_ARCHIVE_VERIFICATIONS_PER_RUN);
    for (const record of unverified) {
      await verifyServerArchiveAndCompact(record.client_uuid).catch(() => 0);
    }
    report = await getLocalStorageLifecycleReport();
  }

  // Dokumente/Kontrollbilder sind Offline-Komfort und werden erst bei echtem
  // Speicherdruck entfernt: nur alte, vollständig serververifizierte Vorgänge,
  // niemals Drafts, niemals ungesicherte oder aktuelle Vorgänge.
  if (report.usageBytes >= report.cleanupBytes) {
    records = await getAllLocalWithdrawals().catch(() => [] as LocalWithdrawalRecord[]);
    await compactVerifiedRecords(records, true);
    report = await getLocalStorageLifecycleReport();
  }

  return report;
}

export async function ensureLocalStorageCaptureCapacity(): Promise<LocalStorageLifecycleReport> {
  // Capture selbst bleibt lokal und schnell: keine Server-Requests im Auslöser.
  // Netzwerk-Verifikation läuft bereits best-effort über den Systemstatus; hier
  // werden nur schon verifizierte lokale Duplikate/Caches sicher bereinigt.
  const report = await runLocalStorageLifecycleMaintenance({
    allowNetworkVerification: false,
    reason: "capture",
  });
  const quotaRisk = Number.isFinite(report.quotaHeadroomBytes) && report.quotaHeadroomBytes < LOCAL_STORAGE_CAPTURE_RESERVE_BYTES;
  if (report.usageBytes >= report.hardGuardBytes || quotaRisk) {
    const protectedText = formatBytes(report.protectedBytes);
    throw new Error(
      `Lokaler Speicher fast voll. ${protectedText} sind aktuell geschützt und werden nicht automatisch gelöscht. `
      + "Bitte Serververbindung/Synchronisierung prüfen oder nicht mehr benötigte lokale Entwürfe entfernen.",
    );
  }
  return report;
}

export function formatBytes(value: number): string {
  if (!Number.isFinite(value)) return "–";
  if (value >= 1024 * MB) return `${(value / (1024 * MB)).toFixed(2)} GB`;
  if (value >= MB) return `${(value / MB).toFixed(1)} MB`;
  if (value >= 1024) return `${Math.round(value / 1024)} KB`;
  return `${Math.max(0, Math.round(value))} B`;
}
